import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Coffee, Heart, Gift, Smartphone, Landmark, ExternalLink, Copy,
  Lock, Unlock, Trash2, Plus, Pencil, Eye, EyeOff, Check, X, ArrowLeft, Sparkles, Play,
} from 'lucide-react';
import Header from '../components/Header';
import ShareButton from '../components/ShareButton';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping (from lucide). Keep small & expressive.
const ICONS = {
  'credit-card': CreditCard,
  paypal: Heart,            // fallback
  coffee: Coffee,
  heart: Heart,
  gift: Gift,
  smartphone: Smartphone,
  landmark: Landmark,
  play: Play,
};

const IconOf = (key) => ICONS[key] || Heart;

/* AdminContext supplied by parent App (5 logo-clicks) */
export const DonationsAdminContext = React.createContext(false);

export default function DonationsPage() {
  const navigate = useNavigate();
  const { setIsAdminGlobal } = useModal() || {};
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminMode, setAdminMode] = useState(false);
  const [highlightGrid, setHighlightGrid] = useState(false);
  const isAdmin = useContext(DonationsAdminContext);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Always include disabled so the grid is never empty; we filter client-side.
      const r = await axios.get(`${API}/api/donation-methods`, {
        params: { include_disabled: true },
      });
      setMethods(r.data || []);
    } catch (e) {
      console.warn('[Donations] refresh failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Show all methods to everyone. Disabled (or no-value) methods render as
  // "Bientôt disponible". This guarantees the grid is never empty and the
  // "Faire un don" CTA always has a visible scroll target.
  const visible = methods;

  const scrollToGrid = useCallback(() => {
    const el = document.getElementById('donation-methods-grid');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightGrid(true);
      window.setTimeout(() => setHighlightGrid(false), 1600);
    } else {
      // Fallback: scroll to bottom of page (grid hasn't mounted yet)
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <Header onAdminAccess={() => setIsAdminGlobal && setIsAdminGlobal(true)} />
      <main className="max-w-3xl mx-auto px-4 pt-28 pb-8" data-testid="donations-page">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 transition"
          data-testid="donations-back">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/30 mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl text-amber-300 mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Soutenir Hypnose clé
          </h1>
          <p className="text-amber-200/90 text-base sm:text-lg italic mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            « Aider son prochain, c'est aussi aider à transmettre ce qui apaise. »
          </p>

          {/* Gros CTA "Faire un don" - scroll vers la grille des méthodes */}
          <button
            onClick={scrollToGrid}
            className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full font-semibold text-base sm:text-lg transition-all hover:scale-105 shadow-xl shadow-pink-500/30 hover:shadow-pink-500/50"
            style={{
              background: 'linear-gradient(135deg, #ec4899, #f43f5e, #fbbf24)',
              color: '#fff',
              border: '1.5px solid rgba(251, 191, 36, 0.5)',
            }}
            data-testid="donations-cta-main"
          >
            <Heart className="w-5 h-5 fill-white" />
            <span>Faire un don</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/25 font-bold">10 façons</span>
          </button>
        </div>

        {/* Incentive block — story + reasons to support */}
        <div className="rounded-3xl border border-amber-400/25 bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950/30 p-6 sm:p-7 mb-8 shadow-xl shadow-amber-500/5"
          data-testid="donations-incentive">

          <p className="text-white/85 leading-relaxed text-sm sm:text-base mb-4">
            Hypnose clé est <b className="text-amber-300">100 % gratuite</b> et le restera. Aucun abonnement,
            aucun contenu verrouillé. Si elle vous accompagne dans vos moments de stress, de doute ou simplement
            dans vos pauses de la journée, votre soutien fait toute la différence.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-2xl bg-black/30 border border-white/5 p-3.5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-300 flex items-center justify-center mb-2">
                <Heart className="w-4 h-4" />
              </div>
              <h3 className="text-amber-200 font-semibold text-sm mb-1">Aider l'application à exister</h3>
              <p className="text-white/60 text-[12px] leading-snug">
                Serveurs, hébergement vidéo, sauvegarde des voix… chaque don aide à garder l'app en ligne et
                accessible à tous.
              </p>
            </div>
            <div className="rounded-2xl bg-black/30 border border-white/5 p-3.5">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center mb-2">
                <Gift className="w-4 h-4" />
              </div>
              <h3 className="text-emerald-200 font-semibold text-sm mb-1">Rembourser l'investissement</h3>
              <p className="text-white/60 text-[12px] leading-snug">
                Des centaines d'heures, des années de formation, du matériel d'enregistrement, des frais
                techniques… votre soutien encourage à poursuivre.
              </p>
            </div>
            <div className="rounded-2xl bg-black/30 border border-white/5 p-3.5">
              <div className="w-9 h-9 rounded-xl bg-pink-500/20 text-pink-300 flex items-center justify-center mb-2">
                <Sparkles className="w-4 h-4" />
              </div>
              <h3 className="text-pink-200 font-semibold text-sm mb-1">Aider son prochain</h3>
              <p className="text-white/60 text-[12px] leading-snug">
                Quand vous soutenez Hypnose clé, vous offrez aussi un moment de calme à toutes les personnes
                qui découvriront l'app après vous.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-amber-500/8 border border-amber-400/20 p-4">
            <p className="text-white/85 text-[13px] sm:text-sm leading-relaxed">
              Cette application s'appuie sur <b className="text-amber-300">des années de travail</b> et
              <b className="text-amber-300"> de nombreux stages</b> dans le bien-être, l'hypnose ericksonienne,
              la relaxation et le développement personnel. Chaque exercice, chaque affirmation, chaque
              respiration a été pensée pour transmettre du calme et de l'apaisement avec sincérité.
            </p>
            <p className="text-amber-100/80 text-[12px] mt-3 italic">
              « Un don, même symbolique, c'est dire : <span className="text-amber-300 not-italic font-semibold">"continue, ça fait du bien."</span> »
            </p>
          </div>
        </div>

        {/* Testimonials carousel — 5 random testimonials from seeds + real reviews */}
        <div className="mb-8">
          <TestimonialsCarousel count={5} />
        </div>

        {/* Admin toggle */}
        {isAdmin && (
          <div className="mb-6 flex items-center justify-between rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-2.5">
            <p className="text-amber-200 text-xs">Mode administrateur</p>
            <button
              onClick={() => setAdminMode((v) => !v)}
              className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-slate-900 font-semibold hover:bg-amber-400"
              data-testid="donations-admin-toggle"
            >
              {adminMode ? 'Quitter l\'édition' : 'Éditer les méthodes'}
            </button>
          </div>
        )}

        {/* List - scroll target ALWAYS rendered so the "Faire un don" CTA never fails */}
        <div
          id="donation-methods-grid"
          data-testid="donation-methods-grid"
          className={`scroll-mt-24 transition-all duration-500 ${highlightGrid ? 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-slate-950 rounded-2xl' : ''}`}
        >
          <div className="flex items-center gap-2 mb-3">
            <Heart className={`w-4 h-4 text-pink-400 ${highlightGrid ? 'animate-pulse' : ''}`} />
            <h2 className="text-amber-200 text-sm font-semibold uppercase tracking-wider">
              Choisissez votre moyen de don
            </h2>
          </div>
          <p className="text-white/55 text-xs mb-4">
            Toutes les méthodes activées par le créateur s'affichent ci-dessous. Les méthodes marquées « Bientôt disponible » seront ouvertes prochainement.
          </p>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={`skel-${i}`} className="h-24 rounded-2xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-white/50 text-sm" data-testid="donations-empty">
              Aucune méthode de don configurée pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visible.map((m) => (
                <DonationCard
                  key={m.id}
                  method={m}
                  adminMode={adminMode}
                  onChange={refresh}
                />
              ))}
            </div>
          )}
        </div>

        {/* Admin: create custom */}
        {adminMode && <CreateCustomMethod onCreated={refresh} />}

        <div className="mt-10 flex flex-col items-center gap-3">
          <ShareButton />
          <p className="text-white/40 text-xs text-center max-w-md">
            Merci du fond du cœur 🩷 — chaque geste compte.
          </p>
        </div>
      </main>
    </div>
  );
}

function DonationCard({ method, adminMode, onChange }) {
  const Icon = IconOf(method.icon);
  const [editing, setEditing] = useState(false);
  const isCopyable = ['iban', 'phone'].includes(method.value_kind);
  const hasValue = !!(method.value && method.value.trim());
  const isExternalUrl = method.value_kind === 'url' && hasValue;
  // For public view, a card is actionable only if enabled AND has a value.
  const isActive = adminMode ? hasValue : (method.enabled && hasValue);

  const doCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copié dans le presse-papier');
    } catch {
      toast.error('Copie impossible');
    }
  };

  const handleOpen = () => {
    if (!hasValue) {
      toast.info('Cette méthode n\'est pas encore configurée');
      return;
    }
    if (isExternalUrl) {
      window.open(method.value, '_blank', 'noopener,noreferrer');
    } else {
      doCopy(method.value);
    }
  };

  return (
    <div
      className="relative rounded-2xl border bg-slate-800 p-4 transition hover:bg-slate-700/80"
      style={{
        borderColor: `${method.color}40`,
        boxShadow: `0 0 0 1px ${method.color}20, 0 6px 20px -10px ${method.color}50`,
      }}
      data-testid={`donation-card-${method.id}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${method.color}30`, color: method.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm leading-tight">{method.label}</h3>
          <p className="text-white/55 text-[11px] mt-0.5 leading-snug">{method.description}</p>
        </div>
        {!adminMode && isActive && (
          <button
            onClick={handleOpen}
            className="text-white/40 hover:text-white"
            title={isExternalUrl ? 'Ouvrir' : 'Copier'}
            data-testid={`donation-action-${method.id}`}
          >
            {isExternalUrl ? <ExternalLink className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        )}
      </div>

      {!adminMode && isActive && !isExternalUrl && (
        <div className="rounded-lg bg-black/40 px-2.5 py-1.5 text-white/80 text-[11px] font-mono truncate"
          title={method.value}>
          {method.value}
        </div>
      )}

      {!adminMode && isActive && method.value_kind === 'iban' && method.extra?.bic && (
        <p className="text-white/50 text-[10px] mt-1">BIC : <span className="font-mono text-white/75">{method.extra.bic}</span></p>
      )}

      {!adminMode && isActive && (
        method.type === 'paypal' ? (
          <PaypalQuickAmounts method={method} />
        ) : (
          <button
            onClick={handleOpen}
            className="mt-3 w-full py-2 rounded-xl text-xs font-semibold transition"
            style={{ backgroundColor: method.color, color: '#fff' }}
            data-testid={`donation-cta-${method.id}`}
          >
            {isExternalUrl ? 'Faire un don' : 'Copier'}
          </button>
        )
      )}

      {!adminMode && !isActive && (
        <div
          className="mt-3 w-full py-2 rounded-xl text-[11px] font-semibold text-center bg-white/5 border border-white/10 text-white/50"
          data-testid={`donation-soon-${method.id}`}
        >
          Bientôt disponible
        </div>
      )}

      {/* Admin controls */}
      {adminMode && (
        <AdminCardControls
          method={method}
          editing={editing}
          setEditing={setEditing}
          onChange={onChange}
        />
      )}
    </div>
  );
}

function AdminCardControls({ method, editing, setEditing, onChange }) {
  const [form, setForm] = useState({
    label: method.label,
    description: method.description,
    value: method.value,
    color: method.color,
    bic: method.extra?.bic || '',
    holder: method.extra?.holder || '',
  });

  const save = async () => {
    const payload = {
      label: form.label,
      description: form.description,
      value: form.value,
      color: form.color,
    };
    if (method.value_kind === 'iban') {
      payload.extra = { bic: form.bic, holder: form.holder };
    }
    try {
      await axios.put(`${API}/api/donation-methods/${method.id}`, payload);
      toast.success('Méthode mise à jour');
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec mise à jour');
    }
  };

  const toggleEnabled = async () => {
    try {
      await axios.put(`${API}/api/donation-methods/${method.id}`, { enabled: !method.enabled });
      toast.success(method.enabled ? 'Désactivée' : 'Activée');
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const toggleLock = async () => {
    try {
      await axios.put(`${API}/api/donation-methods/${method.id}`, { locked: !method.locked });
      toast.success(method.locked ? 'Déverrouillée' : 'Verrouillée');
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer "${method.label}" ?`)) return;
    try {
      await axios.delete(`${API}/api/donation-methods/${method.id}`);
      toast.success('Supprimée');
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  if (editing) {
    return (
      <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
        <input className="w-full px-2 py-1.5 rounded-lg bg-slate-900 text-white text-xs border border-white/10"
          value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Libellé" data-testid={`edit-label-${method.id}`} />
        <textarea className="w-full px-2 py-1.5 rounded-lg bg-slate-900 text-white text-xs border border-white/10 resize-none"
          rows={2} value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Description" data-testid={`edit-desc-${method.id}`} />
        <input className="w-full px-2 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-mono border border-white/10"
          value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
          placeholder={method.value_kind === 'iban' ? 'FR76...' : 'https://...'}
          data-testid={`edit-value-${method.id}`} />
        {method.value_kind === 'iban' && (
          <>
            <input className="w-full px-2 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-mono border border-white/10"
              value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })}
              placeholder="BIC (optionnel)" />
            <input className="w-full px-2 py-1.5 rounded-lg bg-slate-900 text-white text-xs border border-white/10"
              value={form.holder} onChange={(e) => setForm({ ...form, holder: e.target.value })}
              placeholder="Titulaire (optionnel)" />
          </>
        )}
        <div className="flex items-center gap-2">
          <label className="text-white/70 text-[10px]">Couleur</label>
          <input type="color" value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            className="w-8 h-6 rounded cursor-pointer" />
        </div>
        <div className="flex gap-2">
          <button onClick={save}
            className="flex-1 px-2 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-semibold"
            data-testid={`save-${method.id}`}>
            <Check size={12} className="inline mr-1" /> Enregistrer
          </button>
          <button onClick={() => setEditing(false)}
            className="px-2 py-1.5 rounded-lg bg-white/10 text-white/70 text-xs">
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <button onClick={toggleEnabled}
        className={`text-[10px] px-2 py-1 rounded-lg ${method.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/5 text-white/50'}`}
        data-testid={`toggle-enabled-${method.id}`}>
        {method.enabled ? <Eye size={10} className="inline mr-1" /> : <EyeOff size={10} className="inline mr-1" />}
        {method.enabled ? 'Visible' : 'Masquée'}
      </button>
      <button onClick={() => setEditing(true)} disabled={method.locked}
        className="text-[10px] px-2 py-1 rounded-lg bg-white/5 text-white/70 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={`edit-${method.id}`}>
        <Pencil size={10} className="inline mr-1" /> Éditer
      </button>
      <button onClick={toggleLock}
        className={`text-[10px] px-2 py-1 rounded-lg ${method.locked ? 'bg-amber-500 text-slate-900' : 'bg-white/5 text-white/70'}`}
        data-testid={`lock-${method.id}`}>
        {method.locked ? <Lock size={10} /> : <Unlock size={10} />}
      </button>
      <button onClick={remove} disabled={method.locked}
        className="text-[10px] px-2 py-1 rounded-lg bg-red-500/15 text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={`delete-${method.id}`}>
        <Trash2 size={10} />
      </button>
    </div>
  );
}

function PaypalQuickAmounts({ method }) {
  const PRESETS = [5, 10, 20];

  const openPaypal = (amount) => {
    const sep = method.value.includes('?') ? '&' : '?';
    const url = amount ? `${method.value}${sep}amount=${amount}` : method.value;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-3 space-y-2" data-testid={`donation-cta-${method.id}`}>
      <p className="text-white/55 text-[10px] uppercase tracking-wider font-semibold">
        Don suggéré
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        {PRESETS.map((amt) => (
          <button
            key={amt}
            onClick={() => openPaypal(amt)}
            className="py-1.5 rounded-lg text-xs font-bold transition hover:brightness-110 active:scale-95"
            style={{
              backgroundColor: `${method.color}25`,
              color: method.color,
              border: `1px solid ${method.color}55`,
            }}
            data-testid={`donation-paypal-amount-${amt}`}
          >
            {amt}&nbsp;€
          </button>
        ))}
      </div>
      <button
        onClick={() => openPaypal(null)}
        className="w-full py-2 rounded-xl text-xs font-semibold transition hover:brightness-110"
        style={{ backgroundColor: method.color, color: '#fff' }}
        data-testid={`donation-paypal-custom`}
      >
        Autre montant
      </button>
    </div>
  );
}

function CreateCustomMethod({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    label: '', description: '', icon: 'heart',
    color: '#fbbf24', value: '', value_kind: 'url',
  });

  const create = async () => {
    if (!form.label.trim()) {
      toast.error('Libellé requis');
      return;
    }
    try {
      await axios.post(`${API}/api/donation-methods`, { ...form, enabled: false });
      toast.success('Méthode créée. Activez-la depuis la liste.');
      setOpen(false);
      setForm({ label: '', description: '', icon: 'heart', color: '#fbbf24', value: '', value_kind: 'url' });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="mt-4 w-full px-3 py-2.5 rounded-xl border border-dashed border-white/20 text-white/60 hover:text-white hover:border-white/40 text-xs transition"
        data-testid="donations-create-custom">
        <Plus size={12} className="inline mr-1" /> Créer une méthode personnalisée
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-amber-400/30 bg-slate-800 p-4 space-y-2"
      data-testid="donations-create-form">
      <h3 className="text-amber-200 text-sm font-semibold">Nouvelle méthode personnalisée</h3>
      <input className="w-full px-3 py-2 rounded-lg bg-slate-900 text-white text-sm border border-white/10"
        value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
        placeholder="Libellé (ex. Bitcoin)" />
      <input className="w-full px-3 py-2 rounded-lg bg-slate-900 text-white text-sm border border-white/10"
        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Description courte" />
      <input className="w-full px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-mono border border-white/10"
        value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
        placeholder="URL ou adresse" />
      <div className="flex items-center gap-2">
        <select className="px-2 py-1.5 rounded-lg bg-slate-900 text-white text-xs border border-white/10"
          value={form.value_kind} onChange={(e) => setForm({ ...form, value_kind: e.target.value })}>
          <option value="url">URL</option>
          <option value="iban">IBAN</option>
          <option value="phone">Téléphone</option>
          <option value="crypto">Adresse crypto</option>
        </select>
        <input type="color" value={form.color}
          onChange={(e) => setForm({ ...form, color: e.target.value })}
          className="w-10 h-7 rounded cursor-pointer" />
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={create}
          className="flex-1 px-3 py-2 rounded-lg bg-emerald-500 text-slate-900 text-sm font-semibold"
          data-testid="donations-create-submit">
          Créer
        </button>
        <button onClick={() => setOpen(false)}
          className="px-3 py-2 rounded-lg bg-white/10 text-white/70 text-sm">
          Annuler
        </button>
      </div>
    </div>
  );
}
